import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow, useNodes } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { NodeData } from '@/types/workflow';
import { FileText, Copy, Trash2, Plus, X, ChevronDown, ChevronUp, Search, HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ExtractDataNodeProps extends NodeProps {
  data: NodeData;
}

export const ExtractDataNode = memo(({ data, selected, id }: ExtractDataNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState<number | null>(null);
  const [typeSearch, setTypeSearch] = useState('');
  const { getNodes, setNodes, getEdges, setEdges, getNode } = useReactFlow();
  const nodes = useNodes();
  const nodeColor = '#ec4899'; // Pink for AI processing

  // Get connected input fields
  const getConnectedFields = useCallback(() => {
    const edges = getEdges();
    const incomingEdge = edges.find(edge => edge.target === id);
    if (!incomingEdge) return [];
    
    const sourceNode = getNode(incomingEdge.source);
    if (!sourceNode) return [];
    
    // Extract available fields from the source node's outputs
    const outputs = sourceNode.data.outputs || [];
    return outputs.map((output: any) => ({
      id: output.id,
      label: output.label || output.id,
      type: output.type || 'data'
    }));
  }, [id, getEdges, getNode]);

  const connectedFields = getConnectedFields();

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  const extractionFields = data.extractionFields || [];

  const addField = () => {
    const newFields = [...extractionFields, { name: '', type: 'text', description: '' }];
    updateNodeData({ extractionFields: newFields });
  };

  const removeField = (index: number) => {
    const newFields = extractionFields.filter((_: any, i: number) => i !== index);
    updateNodeData({ extractionFields: newFields });
  };

  const updateField = (index: number, field: string, value: string) => {
    const newFields = [...extractionFields];
    newFields[index] = { ...newFields[index], [field]: value };
    updateNodeData({ extractionFields: newFields });
  };

  const dataTypes = [
    { value: 'text', label: 'text' },
    { value: 'number', label: 'number' },
    { value: 'boolean', label: 'boolean' },
    { value: 'date', label: 'date' },
    { value: 'email', label: 'email' },
    { value: 'url', label: 'url' },
    { value: 'phone', label: 'phone' },
    { value: 'array', label: 'array' },
    { value: 'object', label: 'object' }
  ];

  const filteredTypes = dataTypes.filter(type => 
    type.label.toLowerCase().includes(typeSearch.toLowerCase())
  );

  return (
    <TooltipProvider>
      <div 
        className="relative group"
        onMouseEnter={() => setShowToolbar(true)}
        onMouseLeave={() => setShowToolbar(false)}
      >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[380px] backdrop-blur-md bg-background/75 border-pink-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-pink-500/50 shadow-lg shadow-pink-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-pink-100 dark:bg-pink-900/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs px-2 py-0">AI</Badge>
                <span className="text-xs text-muted-foreground">Using AI</span>
                <Badge variant="outline" className="text-xs px-2 py-0 ml-auto">
                  Loop Mode
                </Badge>
              </div>
              <div className="font-semibold text-lg mt-1">Extract Data</div>
              <div className="text-sm text-muted-foreground">
                Extract key pieces of information or a list of information from some input text.
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-4">
          {/* Content to Extract From */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <Label className="text-sm font-medium">Content to Extract From</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">Select which data from the previous node you want to extract information from</p>
                </TooltipContent>
              </Tooltip>
            </div>
            {connectedFields.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="text-sm">
                      {data.selectedField ? data.selectedField : 'Select field to extract from'}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full">
                  {connectedFields.map((field) => (
                    <DropdownMenuItem
                      key={field.id}
                      onClick={() => updateNodeData({ selectedField: field.label })}
                    >
                      <Badge variant="secondary" className="mr-2">{field.label}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Input
                placeholder="Enter sample content for extraction"
                value={data.contentExample || ''}
                onChange={(e) => updateNodeData({ contentExample: e.target.value })}
                className="text-sm"
              />
            )}
          </div>

          {/* Additional Context */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <Label className="text-sm font-medium">Additional Context</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">Provide additional instructions to help the AI understand what to extract</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Textarea
              placeholder="Describe what specific information you want to extract and any special formatting requirements"
              value={data.additionalContext || ''}
              onChange={(e) => updateNodeData({ additionalContext: e.target.value })}
              className="min-h-[80px] resize-none text-sm"
            />
          </div>

          {/* Extract List Toggle */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Label className="text-sm font-medium">Extract List?</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">Enable this to extract multiple items from the content (e.g., a list of contacts from a document)</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                checked={data.extractList === true}
                onCheckedChange={(checked) => updateNodeData({ extractList: checked })}
              />
            </div>
          </div>

          {/* Data Fields */}
          <div>
            <div className="flex items-center gap-1 mb-3">
              <Label className="text-sm font-medium">Data</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">Define the fields you want to extract and their data types</p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            {data.extractList === true ? (
              <div className="space-y-3">
                {extractionFields.map((field: any, index: number) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg relative">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeField(index)}
                      className="absolute top-2 right-2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    
                    <div className="space-y-3 pr-8">
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <Label className="text-sm">Name:</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">The name of the field to extract (e.g., "company_name", "email", "phone")</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Input
                          placeholder="Field name (e.g., company_name)"
                          value={field.name}
                          onChange={(e) => updateField(index, 'name', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <Label className="text-sm">Type:</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">The data type of this field (text, number, email, etc.)</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <DropdownMenu open={typeDropdownOpen === index} onOpenChange={(open) => setTypeDropdownOpen(open ? index : null)}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between h-9"
                            >
                              <span>{field.type || 'text'}</span>
                              <ChevronDown className="w-4 h-4 ml-2" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-full">
                            <div className="p-2">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                  placeholder="Search..."
                                  value={typeSearch}
                                  onChange={(e) => setTypeSearch(e.target.value)}
                                  className="pl-8 h-8"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filteredTypes.map((type) => (
                                <DropdownMenuItem
                                  key={type.value}
                                  onClick={() => {
                                    updateField(index, 'type', type.value);
                                    setTypeDropdownOpen(null);
                                    setTypeSearch('');
                                  }}
                                >
                                  {type.label}
                                </DropdownMenuItem>
                              ))}
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <Label className="text-sm">Description:</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">Optional description to help the AI understand what to extract for this field</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Input
                          placeholder="Optional: Describe what this field contains"
                          value={field.description}
                          onChange={(e) => updateField(index, 'description', e.target.value)}
                          className="h-9"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={addField}
                  className="w-full h-10 border-dashed border-2 hover:border-solid"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => updateNodeData({ extractList: true })}
                className="w-full h-10 text-pink-600 border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950/20"
              >
                <Plus className="w-4 h-4 mr-2 text-pink-600" />
                Add Data
              </Button>
            )}
          </div>

          {/* AI Model Selection */}
          <div>
            <Label className="text-sm font-medium mb-2">Choose AI model</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span>{data.model || 'gpt-4o-mini'}</span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full" side="bottom" align="start">
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">OpenAI</div>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gpt-4o-mini' })}>
                  GPT-4o Mini
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gpt-4o' })}>
                  GPT-4o
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gpt-4-turbo' })}>
                  GPT-4 Turbo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gpt-3.5-turbo' })}>
                  GPT-3.5 Turbo
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Anthropic</div>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'claude-3-haiku' })}>
                  Claude 3 Haiku
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'claude-3-sonnet' })}>
                  Claude 3 Sonnet
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'claude-3-opus' })}>
                  Claude 3 Opus
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Google</div>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gemini-pro' })}>
                  Gemini Pro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateNodeData({ model: 'gemini-1.5-pro' })}>
                  Gemini 1.5 Pro
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Show More Options */}
          <Button
            variant="ghost"
            onClick={() => setShowMoreOptions(!showMoreOptions)}
            className="w-full justify-start text-sm text-muted-foreground hover:text-foreground"
          >
            {showMoreOptions ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                Hide More Options
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Show More Options
              </>
            )}
          </Button>

          {showMoreOptions && (
            <div className="space-y-3 pt-2 border-t">
              <div>
                <Label className="text-sm">Temperature</Label>
                <Input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  placeholder="0.7"
                  value={data.temperature || 0.7}
                  onChange={(e) => updateNodeData({ temperature: parseFloat(e.target.value) })}
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Max Tokens</Label>
                <Input
                  type="number"
                  min="1"
                  max="4000"
                  placeholder="1000"
                  value={data.maxTokens || 1000}
                  onChange={(e) => updateNodeData({ maxTokens: parseInt(e.target.value) })}
                  className="h-9 mt-1"
                />
              </div>
            </div>
          )}
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="custom-handle"
          style={{
            backgroundColor: '#ec4899',
            boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
          }}
        />
      </Card>
      </div>
    </TooltipProvider>
  );
});

ExtractDataNode.displayName = 'ExtractDataNode';